import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { AdminShippingProvider } from '@/services/adminService';

export type ManualTrackingFormState = {
  provider: AdminShippingProvider;
  trackingNumber: string;
  shipmentUuid: string;
};

interface ManualTrackingDialogProps {
  open: boolean;
  form: ManualTrackingFormState;
  status: 'idle' | 'saving' | 'error';
  error: string | null;
  onOpenChange: (open: boolean) => void;
  onFormChange: (next: ManualTrackingFormState) => void;
  onSave: () => void;
}

const ManualTrackingDialog = ({
  open,
  form,
  status,
  error,
  onOpenChange,
  onFormChange,
  onSave,
}: ManualTrackingDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manual shipment tracking</DialogTitle>
          <DialogDescription>
            Save the carrier tracking reference when the shipment was created outside the API.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="manual-tracking-provider">Provider</Label>
            <Select
              value={form.provider}
              onValueChange={(value) => onFormChange({ ...form, provider: value as AdminShippingProvider })}
            >
              <SelectTrigger id="manual-tracking-provider">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UKRPOSHTA">Ukrposhta</SelectItem>
                <SelectItem value="NOVAPOST">NovaPost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="manual-tracking-number">
              {form.provider === 'UKRPOSHTA' ? 'Barcode / tracking number' : 'Tracking number'}
            </Label>
            <Input
              id="manual-tracking-number"
              value={form.trackingNumber}
              onChange={(event) => onFormChange({ ...form, trackingNumber: event.target.value })}
              placeholder={form.provider === 'UKRPOSHTA' ? 'RA123456789UA' : '20450987654321'}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="manual-tracking-shipment-uuid">Shipment UUID / shipmentId</Label>
            <Input
              id="manual-tracking-shipment-uuid"
              value={form.shipmentUuid}
              onChange={(event) => onFormChange({ ...form, shipmentUuid: event.target.value })}
              placeholder="Optional but recommended"
            />
          </div>
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={status === 'saving'}>
              Cancel
            </Button>
            <Button type="button" onClick={onSave} disabled={status === 'saving'}>
              {status === 'saving' ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving
                </span>
              ) : 'Save tracking'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManualTrackingDialog;
